import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Checkbox } from '@mui/material';
import EnhancedDataGrid from '../Common/EnhancedDataGrid';
import { httpGet } from '../../HttpRequestHandling/httpRequestHandler';
import { toast } from 'react-toastify';
import Zoom from '@mui/material/Zoom';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Zoom in={true} ref={ref} {...props} />;
});

const AdTechVendorMappingsLookup = ({
    open,
    onClose,
    preselectedIds = [],          
    existingCurrencyCodes = [],    
    modelDefId = 1,
    inProgress
}) => {
    const [rows, setRows] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [filterText, setFilterText] = useState('');

    const lockedIds = useMemo(() => new Set(preselectedIds.map(String)), [preselectedIds]);
    const usedCurrencies = useMemo(
        () => new Set((existingCurrencyCodes || []).filter(Boolean).map((c) => c.toUpperCase())),
        [existingCurrencyCodes]
    );


    useEffect(() => {
        if (!open || hasLoaded) return;

        (async () => {
            inProgress?.(true);
            try {
                const { data } = await httpGet(`/api/invoicemodels/${modelDefId}?isAdtechIntegration=1`);
                const normalized = (data || []).map((r, idx) => {
                    const id = r.id ?? (idx + 1);
                    const vendorStr = r.vendor || '';
                    let vendorCode = '', vendorName = vendorStr;
                    const parts = vendorStr.split(' - ');
                    if (parts.length >= 2) {
                        vendorCode = parts[0] || '';
                        vendorName = parts.slice(1).join(' - ') || '';
                    }
                    return {
                        id,
                        vendorMappingId: id,
                        modelName: r.documentModelName || '',
                        modelTag: r.documentModelNameTag || '',
                        erpVendor: vendorStr,
                        erpVendorSite: r.siteId || '',
                        currencyCode: r.vendorCurrencyCode || '',
                        vendorCode,
                        vendorName
                    };
                });

                setRows(normalized);
                setHasLoaded(true);
                setSelectedRows([]);
            } catch {
                toast.error('Failed to load vendor mappings.');
            } finally {
                inProgress?.(false);
            }
        })();
    }, [open, modelDefId, hasLoaded]);


    useEffect(() => {
        if (!open) {
            setHasLoaded(false);
            setSelectedRows([]);
            setFilterText('');
        }
    }, [open]);

    const findRowById = (id) => rows.find((r) => String(r.id) === String(id));

    const checkbox_clickHandler = (e) => {
        const rowId = String(e.target.value);
        if (lockedIds.has(rowId)) return; 

        const row = findRowById(rowId);
        if (!row) return;

        const existsIdx = selectedRows.findIndex((sr) => String(sr.id) === rowId);

        if (existsIdx >= 0) {

            const next = [...selectedRows];
            next.splice(existsIdx, 1);
            setSelectedRows(next);
        } else {

            const cur = (row.currencyCode || '').toUpperCase();
            const selectedCurrencies = new Set(
                selectedRows.map((r) => (r.currencyCode || '').toUpperCase())
            );
            if (cur && (usedCurrencies.has(cur) || selectedCurrencies.has(cur))) {
                toast.error(`Currency ${row.currencyCode} already selected.`);
                return;
            }
            setSelectedRows((prev) => [...prev, row]);
        }
    };

    const isRowChecked = (row) =>
        lockedIds.has(String(row.id)) ||
        selectedRows.findIndex((sr) => String(sr.id) === String(row.id)) >= 0;

    const handleSelectClick = () => {

        const toAdd = selectedRows
            .filter((r) => !lockedIds.has(String(r.id)))
            .map((r) => ({
                vendorMappingId: r.vendorMappingId,
                modelName: r.modelName,
                modelTag: r.modelTag,
                erpVendor: r.erpVendor,
                currencyCode: r.currencyCode
            }));

        onClose?.(toAdd);
    };

    const handleClose = () => onClose?.([]);

    return (
        <Dialog open={open} onClose={handleClose} TransitionComponent={Transition} fullWidth maxWidth="lg">
            <DialogTitle>Select Vendor Mapping</DialogTitle>
            <DialogContent>
                <Box sx={{ height: 560, width: '100%', mt: 1 }}>
                    <EnhancedDataGrid
                        rows={rows}
                        columns={[
                            {
                                headerName: ' ',
                                field: 'select',
                                width: 40,
                                renderCell: (rowData) => {
                                    const row = rowData.row;
                                    const idStr = String(row.id);
                                    const locked = lockedIds.has(idStr);
                                    return (
                                        <Checkbox
                                            color="primary"
                                            name="chk"
                                            checked={isRowChecked(row)}
                                            value={idStr}
                                            onClick={checkbox_clickHandler}
                                            disabled={locked}
                                        />
                                    );
                                }
                            },
                            { headerName: 'ID', field: 'vendorMappingId', width: 90 },
                            { headerName: 'Model Name', field: 'modelName', flex: 1, minWidth: 160 },
                            { headerName: 'Model Tag', field: 'modelTag', width: 110 },
                            { headerName: 'ERP Vendor', field: 'erpVendor', flex: 1, minWidth: 220 },
                            { headerName: 'ERP Vendor Site', field: 'erpVendorSite', width: 140 },
                            { headerName: 'Currency', field: 'currencyCode', width: 110 }
                        ]}
                        getRowId={(row) => row.id}
                        disableSelectionOnClick
                        pagination
                        pageSize={10}

                        setFilterText={(text) => setFilterText(text)}
                        sx={{
                            '& .MuiDataGrid-columnHeaderTitle': { fontSize: '13px' },
                            '& .MuiDataGrid-cell': { fontSize: '13px' }
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSelectClick} sx={{ ml: 'auto', fontWeight: 600, textTransform: 'uppercase' }}>
                    Select
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdTechVendorMappingsLookup;
